import axios from 'axios'
import { FIXTURES_ENABLED } from '@/auth/keycloak'
import { installAuthInterceptors } from './interceptors'
import { limitConcurrency } from './concurrency'

/**
 * The client for the WRITE service. A second base URL, not a second app.
 *
 * `internal-admin` is a separate deployable on its own host, for the reasons
 * `docs/WRITE_SEAM.md` gives. From the browser that is one extra origin and
 * nothing else: the same Keycloak token, the same audience, the same
 * interceptors.
 *
 * `/admin-api` in development (Vite proxies it to this worktree's ADMIN_PORT),
 * absolute in production — the same trick `internalClient` uses so no localhost
 * URL can be baked into a production bundle, which `frontend/deploy.sh` greps
 * for anyway.
 */
export const adminClient = axios.create({
  baseURL: import.meta.env.VITE_INTERNAL_ADMIN_API ?? '/admin-api',
  // 20 s. Shorter than the read client's 35: the server's own ladder tops out
  // at REQUEST_TIMEOUT 10 s and a function timeout of 15 s, so a write still
  // open at 20 s is not slow — it is lost, and waiting longer only delays the
  // error on a screen where the operator is deciding whether to try again.
  timeout: 20_000,
})

/**
 * In fixture mode the write client REFUSES, rather than pretending.
 *
 * `VITE_INTERNAL_FIXTURES=1` exists so the app can be developed with no network
 * path to production at all — the read client answers every request from a
 * local adapter. Doing that for writes would mean a confirmation dialog that
 * says a price changed when nothing did, which is a worse lie than an error:
 * the whole screen exists to make an operator confident about a real change.
 */
if (FIXTURES_ENABLED) {
  adminClient.interceptors.request.use(() => {
    throw new Error(
      'El modo de datos de muestra no escribe. Corre `make admin` con el túnel de ESCRITURA ' +
        '(`make tunnel TARGET=rw`) para usar esta pantalla.',
    )
  })
} else {
  // Two at a time, as many as the write service's Lambda runs (`concurrency.ts`).
  // Before the auth interceptors: see there for why the order matters.
  limitConcurrency(adminClient, 2)
  installAuthInterceptors(adminClient)
}
